import React, { useCallback, useState } from "react";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

export interface Props {
  filesNumber: number;
  onClickSelectFiles: (files?: FileList | File[]) => Promise<void>;
  onClickClearFiles: () => void;
  selectFilesLoading?: boolean;
}

export const FilesSelect: React.FC<Props> = ({
  filesNumber,
  onClickSelectFiles,
  onClickClearFiles,
  selectFilesLoading = false
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (selectFilesLoading) return;
      const files = e.dataTransfer.files;
      if (files?.length) onClickSelectFiles(files);
    },
    [onClickSelectFiles, selectFilesLoading]
  );

  const handleDropZoneClick = useCallback(() => {
    if (selectFilesLoading) return;
    onClickSelectFiles();
  }, [onClickSelectFiles, selectFilesLoading]);

  return (
    <>
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleDropZoneClick}
        sx={{
          bgcolor: isDragOver ? "#e8f0f8" : "#f6f8fc",
          border: `1px dashed ${isDragOver ? "#26A6AE" : "#dbe8f5"}`,
          borderRadius: "6px",
          height: "217px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "14px",
          cursor: selectFilesLoading ? "default" : "pointer",
          transition: "background-color 0.2s, border-color 0.2s"
        }}
      >
        {selectFilesLoading ? (
          <CircularProgress size={24} sx={{ color: "#071f35" }} />
        ) : (
          <>
            <CloudUploadIcon sx={{ width: "48px", height: "48px", color: "#92A7BC" }} />
            <Stack alignItems="center" gap={0.5}>
              <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#071f35" }}>
                Drag &amp; drop files
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#496682" }}>or click to browse from your computer</Typography>
            </Stack>
          </>
        )}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClickSelectFiles();
          }}
          disabled={selectFilesLoading}
          variant="contained"
          startIcon={selectFilesLoading ? null : <DescriptionOutlinedIcon />}
          sx={{
            bgcolor: "#071f35",
            color: "white",
            borderRadius: "32px",
            textTransform: "none",
            fontSize: "16px",
            "&:hover": { bgcolor: "#0a3142" }
          }}
        >
          Select Files
        </Button>
      </Box>

      {filesNumber > 0 && (
        <Box sx={{ bgcolor: "#f6f8fc", py: "10px" }}>
          <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", px: "10px" }}>
            <Typography sx={{ fontSize: "15px", color: "#071f35", width: "155px" }}>
              {filesNumber} {filesNumber === 1 ? "file" : "files"} added
            </Typography>
            <Button
              variant="outlined"
              sx={{
                borderColor: "#7b92a9",
                color: "#071f35",
                fontSize: "12px",
                borderRadius: "32px"
              }}
              onClick={onClickClearFiles}
            >
              Clear all
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
};
